/**
 * Shared React Query client + persisted-cache key.
 *
 * Extracted from App.tsx so AuthContext (which lives OUTSIDE the
 * PersistQueryClientProvider, see main.tsx) can wipe both the in-memory
 * cache and the localStorage-persisted copy when the signed-in user
 * changes. Without that wipe, one user's cached query data (e.g. the
 * navbar client list, dashboard tiles) survives logout and is served to
 * the next user on the same browser for up to staleTime (15 min) —
 * reproduced 2026-07-29 when a GFD client login left an admin session
 * showing only GFD in the client selector, and the reverse direction
 * would leak admin cross-client data to a client user.
 */
import { QueryClient } from '@tanstack/react-query'

/** localStorage key the sync-storage persister writes the cache under. */
export const RQ_PERSIST_KEY = 'ob-rq-cache-v1'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Treat data as fresh for 15 min — within that window, navigations
      // and re-mounts of a query don't refetch.
      staleTime: 15 * 60 * 1000,
      // Keep cached data for 24h so a refresh / reopened tab can render
      // it instantly while a background revalidation runs.
      gcTime: 24 * 60 * 60 * 1000,
      // Don't auto-refetch on tab focus — the persisted cache + 15-min
      // staleTime means most focus events are no-ops; this also stops
      // sluggish UI when a heavy dashboard tab is left open.
      refetchOnWindowFocus: false,
      // Retry once instead of three times to avoid blocking the spinner
      // for ~10s on a transient network blip.
      retry: 1,
    },
  },
})

/**
 * Wipe every cached query result — in-memory AND the persisted
 * localStorage copy. Called by AuthContext when the authenticated user
 * changes (and on sign-out) so no query data crosses user sessions.
 */
export function clearQueryCachesForUserChange(): void {
  try {
    localStorage.removeItem(RQ_PERSIST_KEY)
  } catch {
    // localStorage can throw in private mode — the in-memory clear below
    // still prevents cross-user reads in this tab.
  }
  queryClient.clear()
}
